import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { IoPeople, IoCart, IoCash, IoTime, IoTrophyOutline, IoEyeOutline, IoCloseOutline, IoCheckmarkCircle, IoEllipseOutline, IoCubeOutline, IoMegaphoneOutline } from 'react-icons/io5';
import { collection, getDocs, doc, setDoc, getDoc, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { ORDER_STATUS } from '../../../config/appConfig';

const formatAmount = (won) => {
  if (won >= 10000) return `${(won / 10000).toFixed(won % 10000 === 0 ? 0 : 1)}억`;
  if (won >= 1) return `${won.toLocaleString()}만`;
  return '0';
};

const getMonthLabel = (date) => `${date.getMonth() + 1}월`;

const fmtDateTime = (ts) => {
  if (!ts) return '-';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const RANK_COLORS = ['#C5A55A', '#8C9AA4', '#A0755A', '#D9DDD6', '#D9DDD6'];
const CACHE_KEY = 'adminDashboardCache';

const loadCache = () => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};
const saveCache = (data) => {
  try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch {}
};

const buildDashboardData = async () => {
  const [usersSnap, constructionsSnap, exposureSnap, ordersSnap, productsSnap, schedulesSnap, noticesSnap] = await Promise.all([
    getDocs(collection(db, 'users')),
    getDocs(collection(db, 'constructions')),
    getDoc(doc(db, 'settings', 'partnerExposure')),
    getDocs(query(collection(db, 'orders'), orderBy('createdAt', 'desc'))),
    getDocs(collection(db, 'products')),
    getDocs(collection(db, 'schedules')),
    getDocs(collection(db, 'notices')),
  ]);
  const totalProducts = productsSnap.size;
  const totalNotices = noticesSnap.size;
  const now = new Date();
  const thisMonth = now.getMonth();
  const thisYear = now.getFullYear();

  // --- 금일 일정 ---
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const allSchedules = schedulesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  const todaySchedules = allSchedules.filter(s => s.date === todayStr);
  const regionMap = {};
  todaySchedules.forEach(s => {
    const addr = s.address || '미지정';
    const region = addr.split(' ').slice(0, 2).join(' ') || '미지정';
    const company = s.companyName || '미지정';
    const key = `${company} · ${region}`;
    regionMap[key] = (regionMap[key] || 0) + 1;
  });
  const todayRegions = Object.entries(regionMap)
    .sort((a, b) => b[1] - a[1])
    .map(([region, count]) => ({ region, count }));

  const exposureData = exposureSnap.exists() ? exposureSnap.data().exposed || {} : {};

  const users = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  const totalUsers = users.length;
  const pendingUsers = users.filter(u => u.status === 'pending').length;
  const thisMonthUsers = users.filter(u => {
    const c = u.createdAt?.toDate?.();
    return c && c.getMonth() === thisMonth && c.getFullYear() === thisYear;
  }).length;

  const constructions = constructionsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

  const companyMap = {};
  constructions.forEach(c => {
    const key = c.company || '미지정';
    if (!companyMap[key]) companyMap[key] = { company: key, amount: 0, count: 0 };
    companyMap[key].amount += (c.amount || 0);
    companyMap[key].count += 1;
  });
  const allPartners = Object.values(companyMap)
    .sort((a, b) => b.amount - a.amount)
    .map((p, i) => ({
      ...p,
      id: `p${i}`,
      exposed: exposureData[p.company] !== undefined ? exposureData[p.company] : true,
    }));

  const thisMonthConstructions = constructions.filter(c => {
    const d = c.createdAt?.toDate?.() || (c.startDate ? new Date(c.startDate) : null);
    return d && d.getMonth() === thisMonth && d.getFullYear() === thisYear;
  });
  const thisMonthAmount = thisMonthConstructions.reduce((s, c) => s + (c.amount || 0), 0);
  const thisMonthCount = thisMonthConstructions.length;

  const amountByMonth = {};
  const countByMonth = {};
  for (let i = 5; i >= 0; i--) {
    const d = new Date(thisYear, thisMonth - i, 1);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    amountByMonth[key] = { month: getMonthLabel(d), value: 0 };
    countByMonth[key] = { month: getMonthLabel(d), value: 0 };
  }
  constructions.forEach(c => {
    const d = c.createdAt?.toDate?.() || (c.startDate ? new Date(c.startDate) : null);
    if (!d) return;
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    if (amountByMonth[key]) {
      amountByMonth[key].value += Math.round((c.amount || 0) / 10000);
      countByMonth[key].value += 1;
    }
  });

  const allOrders = ordersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  const recent5 = allOrders.slice(0, 5).map(o => {
    const statusInfo = ORDER_STATUS[o.status] || { label: o.status, color: '#8C9AA4' };
    const productName = o.items?.length > 0
      ? (o.items.length === 1 ? o.items[0].name : `${o.items[0].name} 외 ${o.items.length - 1}건`)
      : '-';
    return {
      id: o.orderNo || o.id.slice(0, 12),
      company: o.companyName || '-',
      product: productName,
      amount: o.totalAmount ? formatAmount(Math.round(o.totalAmount / 10000)) : '-',
      status: statusInfo.label,
      statusColor: statusInfo.color,
      date: fmtDateTime(o.createdAt),
      statusChangedAt: fmtDateTime(o.statusChangedAt),
    };
  });

  const summary = {};
  Object.keys(ORDER_STATUS).forEach(k => { summary[k] = 0; });
  allOrders.forEach(o => { if (summary[o.status] !== undefined) summary[o.status] += 1; });

  const thisMonthOrders = allOrders.filter(o => {
    const d = o.createdAt?.toDate?.();
    return d && d.getMonth() === thisMonth && d.getFullYear() === thisYear;
  });

  return {
    todayTotal: todaySchedules.length,
    todayRegions,
    partners: allPartners,
    top5: allPartners.slice(0, 5).map((p, i) => ({ ...p, rank: i + 1 })),
    monthlyAmount: Object.values(amountByMonth),
    monthlyCount: Object.values(countByMonth),
    recentOrders: recent5,
    orderSummary: summary,
    stats: [
      { label: '등록 공지', value: `${totalNotices}건`, sub: '공지사항 등록 수', color: '#E67E22' },
      { label: '총 회원수', value: `${totalUsers}명`, sub: thisMonthUsers > 0 ? `+${thisMonthUsers} 이번달` : '이번달 신규 없음', color: '#4A6741' },
      { label: '승인 대기', value: `${pendingUsers}건`, sub: pendingUsers > 0 ? '즉시 처리 필요' : '처리 완료', color: '#E85830' },
      { label: '등록 상품', value: `${totalProducts}개`, sub: '자재상품 등록 수', color: '#6B5B7B' },
      { label: '이번달 주문', value: `${thisMonthOrders.length}건`, sub: allOrders.length > 0 ? `전체 ${allOrders.length}건` : '주문 없음', color: '#5C7A8A' },
      { label: '이번달 매출', value: `${formatAmount(Math.round(thisMonthAmount / 10000))}원`, sub: `${thisMonthCount}건 시공`, color: '#C5A55A' },
    ],
  };
};

const STAT_ICONS = [IoMegaphoneOutline, IoPeople, IoTime, IoCubeOutline, IoCart, IoCash];

const AdminDashboardPage = () => {
  const cached = loadCache();
  const [showExposureModal, setShowExposureModal] = useState(false);
  const [partners, setPartners] = useState(cached?.partners || []);
  const [top5, setTop5] = useState(cached?.top5 || []);
  const [monthlyAmount, setMonthlyAmount] = useState(cached?.monthlyAmount || []);
  const [monthlyCount, setMonthlyCount] = useState(cached?.monthlyCount || []);
  const [todayTotal, setTodayTotal] = useState(cached?.todayTotal || 0);
  const [todayRegions, setTodayRegions] = useState(cached?.todayRegions || []);
  const [recentOrders, setRecentOrders] = useState(cached?.recentOrders || []);
  const [orderSummary, setOrderSummary] = useState(cached?.orderSummary || {});
  const [stats, setStats] = useState(cached?.stats || [
    { label: '등록 공지', value: '0건', sub: '공지사항 등록 수', color: '#E67E22' },
    { label: '총 회원수', value: '0명', sub: '이번달 신규 없음', color: '#4A6741' },
    { label: '승인 대기', value: '0건', sub: '처리 완료', color: '#E85830' },
    { label: '등록 상품', value: '0개', sub: '자재상품 등록 수', color: '#6B5B7B' },
    { label: '이번달 주문', value: '0건', sub: '주문 없음', color: '#5C7A8A' },
    { label: '이번달 매출', value: '0', sub: '0건 시공', color: '#C5A55A' },
  ]);

  const applyData = (data) => {
    setTodayTotal(data.todayTotal);
    setTodayRegions(data.todayRegions);
    setPartners(data.partners);
    setTop5(data.top5);
    setMonthlyAmount(data.monthlyAmount);
    setMonthlyCount(data.monthlyCount);
    setRecentOrders(data.recentOrders);
    setOrderSummary(data.orderSummary);
    setStats(data.stats);
    saveCache(data);
  };

  useEffect(() => {
    // 캐시 유무 관계없이 즉시 최신 데이터 로드
    buildDashboardData().then(applyData);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleExposure = (id) => {
    setPartners(prev => prev.map(p => p.id === id ? { ...p, exposed: !p.exposed } : p));
  };

  const saveExposure = async () => {
    const exposed = {};
    partners.forEach(p => { exposed[p.company] = p.exposed; });
    await setDoc(doc(db, 'settings', 'partnerExposure'), { exposed, updatedAt: new Date() });
    setShowExposureModal(false);
  };

  const maxAmountChart = Math.max(...monthlyAmount.map(s => s.value), 1);
  const maxCountChart = Math.max(...monthlyCount.map(s => s.value), 1);

  return (
  <Container>
    <StatsGrid>
      {stats.map(({ label, value, sub, color }, i) => {
        const Icon = STAT_ICONS[i];
        return (
        <StatCard key={label} $dim={value === '0' || value === '0건' || value === '0개' || value === '0명' || value === '0원'}>
          <StatLeft>
            <StatIcon $color={color} $dim={value === '0' || value === '0건' || value === '0개' || value === '0명' || value === '0원'}><Icon size={24} /></StatIcon>
            <StatLabel $dim={value === '0' || value === '0건' || value === '0개' || value === '0명' || value === '0원'}>{label}</StatLabel>
          </StatLeft>
          <StatRight>
            <StatValue $dim={value === '0' || value === '0건' || value === '0개' || value === '0명' || value === '0원'}>{value}</StatValue>
            <StatSub $dim={value === '0' || value === '0건' || value === '0개' || value === '0명' || value === '0원'}>{sub}</StatSub>
          </StatRight>
        </StatCard>
        );
      })}
    </StatsGrid>

    {/* 금일 일정 */}
    <TodayRow>
      <TodayLabel>금일 일정</TodayLabel>
      <TodayTotal>{todayTotal}건</TodayTotal>
      <TodayDivider />
      {todayRegions.length === 0 ? (
        <TodayRegionTag>일정 없음</TodayRegionTag>
      ) : todayRegions.map(r => (
        <TodayRegionTag key={r.region}>{r.region} <TodayRegionCount>{r.count}</TodayRegionCount></TodayRegionTag>
      ))}
    </TodayRow>

    {/* 주문 상태 요약 */}
    <OrderStatusRow>
      <OrderTab $active={Object.values(orderSummary).reduce((a, b) => a + b, 0) > 0}>전체 {Object.values(orderSummary).reduce((a, b) => a + b, 0)}</OrderTab>
      {Object.entries(ORDER_STATUS).map(([key, { label }]) => (
        <OrderTab key={key} $active={(orderSummary[key] || 0) > 0}>{label} {orderSummary[key] || 0}</OrderTab>
      ))}
    </OrderStatusRow>

    <BottomGrid>
      {/* 최근 주문 */}
      <Section>
        <SectionTitle>최근 주문</SectionTitle>
        <Table>
          <thead>
            <Tr><Th style={{width:'18%'}}>주문번호</Th><Th style={{width:'10%'}}>업체</Th><Th>상품</Th><Th style={{width:'7%'}}>금액</Th><Th style={{width:'16%'}}>주문요청일</Th><Th style={{width:'16%'}}>상태변경일</Th><Th style={{width:'10%'}}>상태</Th></Tr>
          </thead>
          <tbody>
            {recentOrders.length === 0 ? (
              <Tr><Td colSpan={7} style={{ textAlign: 'center', color: '#8C9AA4', padding: '20px 0' }}>주문 데이터 없음</Td></Tr>
            ) : recentOrders.map(o => (
              <Tr key={o.id}>
                <Td>{o.id}</Td>
                <Td>{o.company}</Td>
                <Td>{o.product}</Td>
                <Td>{o.amount}</Td>
                <Td style={{ fontSize: '11px', color: '#8C9AA4', whiteSpace: 'nowrap' }}>{o.date}</Td>
                <Td style={{ fontSize: '11px', color: '#8C9AA4', whiteSpace: 'nowrap' }}>{o.statusChangedAt}</Td>
                <Td><Badge $color={o.statusColor}>{o.status}</Badge></Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </Section>

      <RankSection>
        <RankCard>
          <RankHeader>
            <IoTrophyOutline size={16} color="#C5A55A" />
            <RankTitle>시공액 TOP 5</RankTitle>
            <ExposureBtn onClick={() => setShowExposureModal(true)}>
              <IoEyeOutline size={13} /> 노출 여부 결정
            </ExposureBtn>
          </RankHeader>
          <RankTable>
            <thead><Tr><RTh>순위</RTh><RTh>업체명</RTh><RTh style={{ textAlign: 'right' }}>시공액</RTh><RTh style={{ textAlign: 'right' }}>건수</RTh></Tr></thead>
            <tbody>
              {top5.length === 0 ? (
                <Tr><RTd colSpan={4} style={{ textAlign: 'center', color: '#8C9AA4', padding: '20px 0' }}>시공 데이터 없음</RTd></Tr>
              ) : top5.map(r => {
                const isExposed = partners.find(p => p.company === r.company)?.exposed !== false;
                return (
                <Tr key={r.rank}><RTd><RankCell><RankBadge $color={RANK_COLORS[r.rank - 1]}>{r.rank}</RankBadge><ExposureBadge $on={isExposed}>{isExposed ? '노출' : '비노출'}</ExposureBadge></RankCell></RTd><RTd>{r.company}</RTd><RTd style={{ textAlign: 'right' }}>{formatAmount(Math.round(r.amount / 10000))}</RTd><RTd style={{ textAlign: 'right', color: '#8C9AA4' }}>{r.count}건</RTd></Tr>
                );
              })}
            </tbody>
          </RankTable>
        </RankCard>
      </RankSection>

    </BottomGrid>

    <ChartGrid>
      {/* 월간 시공액 차트 */}
      <Section>
        <SectionTitle>월간 시공액</SectionTitle>
        {monthlyAmount.every(s => s.value === 0) ? (
          <EmptyChart>시공액 데이터 없음</EmptyChart>
        ) : (
          <ChartArea>
            {monthlyAmount.map(s => (
              <BarCol key={s.month}>
                <BarValue>{s.value > 0 ? `${s.value}만` : '-'}</BarValue>
                <Bar $h={Math.round((s.value / maxAmountChart) * 80) || 2} />
                <BarLabel>{s.month}</BarLabel>
              </BarCol>
            ))}
          </ChartArea>
        )}
      </Section>

      {/* 월간 시공 횟수 차트 */}
      <Section>
        <SectionTitle>월간 시공 횟수</SectionTitle>
        {monthlyCount.every(s => s.value === 0) ? (
          <EmptyChart>시공 횟수 데이터 없음</EmptyChart>
        ) : (
          <ChartArea>
            {monthlyCount.map(s => (
              <BarCol key={s.month}>
                <BarValue>{s.value > 0 ? `${s.value}건` : '-'}</BarValue>
                <Bar $h={Math.round((s.value / maxCountChart) * 80) || 2} $color2 />
                <BarLabel>{s.month}</BarLabel>
              </BarCol>
            ))}
          </ChartArea>
        )}
      </Section>
    </ChartGrid>

    {showExposureModal && (
      <ModalOverlay onClick={() => setShowExposureModal(false)}>
        <ModalBox onClick={e => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>파트너 시공 집계 노출 설정</ModalTitle>
            <ModalClose onClick={() => setShowExposureModal(false)}><IoCloseOutline size={20} /></ModalClose>
          </ModalHeader>
          <ModalDesc>선택된 파트너의 시공 집계가 사용자에게 노출됩니다.</ModalDesc>
          <ModalList>
            {partners.map(p => (
              <ModalRow key={p.id} onClick={() => toggleExposure(p.id)}>
                <ToggleIcon $on={p.exposed}>
                  {p.exposed ? <IoCheckmarkCircle size={20} /> : <IoEllipseOutline size={20} />}
                </ToggleIcon>
                <ModalCompany>{p.company}</ModalCompany>
                <ModalStat>{formatAmount(Math.round(p.amount / 10000))}</ModalStat>
                <ModalStat $sub>{p.count}건</ModalStat>
              </ModalRow>
            ))}
          </ModalList>
          <ModalFooter>
            <ModalFooterInfo>노출 {partners.filter(p => p.exposed).length}개 / 전체 {partners.length}개</ModalFooterInfo>
            <ModalSaveBtn onClick={saveExposure}>저장</ModalSaveBtn>
          </ModalFooter>
        </ModalBox>
      </ModalOverlay>
    )}

  </Container>
  );
};

export default AdminDashboardPage;

const Container = styled.div`padding: 32px;`;

const StatsGrid = styled.div`
  display: grid; grid-template-columns: repeat(6, 1fr); gap: 14px; margin-bottom: 14px;
`;
const StatCard = styled.div`
  background: ${p => p.$dim ? '#fff' : '#1A3A5C'};
  border-radius: 12px; padding: 22px 16px;
  display: flex; gap: 12px; align-items: center;
  box-shadow: 0 2px 8px rgba(30, 58, 95, 0.2);
`;
const StatLeft = styled.div`
  display: flex; align-items: center; gap: 8px;
`;
const StatRight = styled.div`
  margin-left: auto; text-align: right;
`;
const StatIcon = styled.div`
  color: ${p => p.$dim ? '#222' : 'rgba(255,255,255,0.7)'};
  display: flex; align-items: center; flex-shrink: 0;
`;
const StatLabel = styled.div`font-size: 13px; color: ${p => p.$dim ? '#222' : 'rgba(255,255,255,0.85)'};`;
const StatValue = styled.div`font-size: 22px; font-weight: 500; color: ${p => p.$dim ? '#222' : '#fff'}; line-height: 1.2;`;
const StatSub = styled.div`font-size: 10px; color: ${p => p.$dim ? '#555' : 'rgba(255,255,255,0.5)'}; margin-top: 2px;`;

const TodayRow = styled.div`
  display: flex; align-items: center; gap: 14px; margin-bottom: 14px;
  background: #fff; border-radius: 12px; padding: 16px 22px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
`;
const TodayLabel = styled.span`font-size: 15px; font-weight: 500; color: #1E2A32;`;
const TodayTotal = styled.span`font-size: 18px; font-weight: 500; color: #E85830;`;
const TodayDivider = styled.div`width: 1px; height: 22px; background: #E0E0E0;`;
const TodayRegionTag = styled.span`
  font-size: 13px; padding: 6px 14px; border-radius: 8px;
  background: #F4F3F8; color: #5C7A8A; font-weight: 400;
`;
const TodayRegionCount = styled.span`font-weight: 500; color: #2C3640;`;

const OrderStatusRow = styled.div`
  display: flex; gap: 6px; margin-bottom: 14px; flex-wrap: wrap;
  background: #fff; border-radius: 12px; padding: 12px 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
`;
const OrderTab = styled.button`
  padding: 10px 16px; border-radius: 10px; font-size: 13px; font-weight: 500; border: none; white-space: nowrap;
  background: ${p => p.$active ? '#4A6741' : '#fff'};
  color: ${p => p.$active ? '#fff' : '#bbb'};
`;

const BottomGrid = styled.div`
  display: grid; grid-template-columns: 7fr 3fr; gap: 14px;
`;
const ChartGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-top: 14px;
`;
const Section = styled.div`
  background: #fff; border-radius: 12px; padding: 16px 18px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  min-height: 240px;
`;
const SectionTitle = styled.h3`font-size: 15px; font-weight: 500; color: #1E2A32; margin-bottom: 10px;`;

const Table = styled.table`width: 100%; border-collapse: collapse; table-layout: fixed;`;
const Tr = styled.tr`
  position: relative; transition: all 0.15s;
  &:not(:last-child) td { border-bottom: 1px solid #F0F0EC; }
  &:hover td { background: #EDF2EB; }
  &:hover td:first-child { box-shadow: inset 3px 0 0 #4A6741; }
  &:active td { background: #E3EBDF; }
`;
const Th = styled.th`text-align: left; font-size: 13px; color: #8C9AA4; font-weight: 400; padding: 8px 8px; border-bottom: 1px solid #E8E8E4; background: #FAFAF7;`;
const Td = styled.td`font-size: 14px; color: #1E2A32; padding: 10px 8px; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;`;
const Badge = styled.span`
  font-size: 13px; padding: 5px 14px; border-radius: 10px; font-weight: 500;
  background: ${p => p.$color}20; color: ${p => p.$color};
`;

const UserRow = styled.div`display: flex; align-items: center; gap: 10px; padding: 8px 0; &:not(:last-child) { border-bottom: 1px solid #F0F0EC; }`;
const UserAvatar = styled.div`
  width: 30px; height: 30px; border-radius: 8px; background: #EDF2EB;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 500; color: #4A6741;
`;
const UserInfo = styled.div`flex: 1;`;
const UserName = styled.div`font-size: 13px; font-weight: 400; color: #1E2A32;`;
const UserSub = styled.div`font-size: 10px; color: #8C9AA4; margin-top: 1px;`;
const UserStatus = styled.span`
  font-size: 10px; padding: 2px 8px; border-radius: 8px; font-weight: 400;
  background: ${p => p.$pending ? '#FFF3E0' : '#EDF2EB'};
  color: ${p => p.$pending ? '#E85830' : '#4A6741'};
`;

const ChartArea = styled.div`display: flex; align-items: flex-end; gap: 16px; justify-content: space-around; padding: 8px 0;`;
const BarCol = styled.div`display: flex; flex-direction: column; align-items: center; gap: 4px;`;
const BarValue = styled.div`font-size: 10px; font-weight: 400; color: #4A6741;`;
const Bar = styled.div`
  width: 40px; height: ${p => p.$h}px; border-radius: 5px 5px 0 0;
  background: ${p => p.$color2
    ? 'linear-gradient(180deg, #5C7A8A 0%, #8BA8B8 100%)'
    : 'linear-gradient(180deg, #4A6741 0%, #6B8F62 100%)'};
`;
const BarLabel = styled.div`font-size: 11px; color: #8C9AA4;`;
const EmptyChart = styled.div`
  display: flex; align-items: center; justify-content: center;
  height: 120px; color: #8C9AA4; font-size: 13px;
`;

/* TOP5 랭킹 */
const RankSection = styled.div`
  display: flex; flex-direction: column; gap: 14px;
  min-height: 240px;
`;
const RankCard = styled.div`
  background: #fff; border-radius: 12px; padding: 16px 18px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  height: 100%;
`;
const RankHeader = styled.div`display: flex; align-items: center; gap: 6px; margin-bottom: 10px;`;
const RankTitle = styled.h3`font-size: 15px; font-weight: 500; color: #1E2A32;`;
const RankTable = styled.table`width: 100%; border-collapse: collapse;`;
const RTh = styled.th`text-align: left; font-size: 13px; color: #8C9AA4; font-weight: 400; padding: 7px 6px; border-bottom: 1px solid #E8E8E4; &:first-child { padding-left: 2px; width: 50px; }`;
const RTd = styled.td`font-size: 14px; color: #1E2A32; padding: 9px 6px; font-weight: 300; b { font-weight: 400; } &:first-child { padding-left: 2px; }`;
const RankBadge = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  width: 20px; height: 20px; border-radius: 5px; font-size: 11px; font-weight: 500;
  background: ${p => p.$color}; color: #fff;
`;

const RankCell = styled.div`
  display: flex; align-items: center; gap: 6px;
`;
const ExposureBadge = styled.span`
  font-size: 9px; padding: 1px 5px; border-radius: 4px; font-weight: 400; white-space: nowrap;
  background: ${p => p.$on ? '#4A6741' : '#E8E8E8'};
  color: ${p => p.$on ? '#fff' : '#A0A0A0'};
`;

const ExposureBtn = styled.button`
  margin-left: auto; display: flex; align-items: center; gap: 4px;
  padding: 4px 10px; border-radius: 6px; font-size: 11px; font-weight: 400;
  background: #F4F3F8; color: #5C7A8A; border: none; cursor: pointer;
  &:hover { background: #E8E7ED; }
`;

const ModalOverlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.35); z-index: 1000;
  display: flex; align-items: center; justify-content: center;
`;
const ModalBox = styled.div`
  background: #fff; border-radius: 14px; width: 440px; max-height: 80vh;
  display: flex; flex-direction: column; box-shadow: 0 8px 32px rgba(0,0,0,0.15);
`;
const ModalHeader = styled.div`
  display: flex; align-items: center; padding: 18px 20px 0; gap: 8px;
`;
const ModalTitle = styled.h3`font-size: 15px; font-weight: 500; color: #1E2A32;`;
const ModalClose = styled.button`
  margin-left: auto; background: none; border: none; color: #8C9AA4; cursor: pointer;
  display: flex; align-items: center; &:hover { color: #1E2A32; }
`;
const ModalDesc = styled.div`
  font-size: 12px; color: #8C9AA4; padding: 6px 20px 12px;
`;
const ModalList = styled.div`
  flex: 1; overflow-y: auto; padding: 0 20px;
`;
const ModalRow = styled.div`
  display: flex; align-items: center; gap: 10px; padding: 10px 0; cursor: pointer;
  &:not(:last-child) { border-bottom: 1px solid #F0F0EC; }
  &:hover { background: #FAFAF7; margin: 0 -20px; padding: 10px 20px; }
`;
const ToggleIcon = styled.div`
  color: ${p => p.$on ? '#4A6741' : '#D0D0D0'}; display: flex; align-items: center;
`;
const ModalCompany = styled.div`font-size: 13px; color: #1E2A32; flex: 1;`;
const ModalStat = styled.div`
  font-size: 12px; color: ${p => p.$sub ? '#8C9AA4' : '#1E2A32'}; min-width: 60px; text-align: right;
`;
const ModalFooter = styled.div`
  display: flex; align-items: center; padding: 14px 20px; border-top: 1px solid #F0F0EC;
`;
const ModalFooterInfo = styled.div`font-size: 12px; color: #8C9AA4;`;
const ModalSaveBtn = styled.button`
  margin-left: auto; padding: 8px 24px; border-radius: 8px; font-size: 13px; font-weight: 400;
  background: #4A6741; color: #fff; border: none; cursor: pointer;
  &:hover { background: #3D5636; }
`;
